import React, { Fragment, useMemo } from 'react'
import type { Offer as OfferType, SelectionScreenInfos } from 'shared/types'
import styled, { css } from 'styled-components'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme/colors'
import { H3, styleH3 } from 'app/common/typography'
import { media } from 'app/assets/styles'
import { Offer } from './offer'
import { FormattedMessage } from 'react-intl'
import { doesPromotionHaveAvailableProductsOrMenus } from 'shared/utils/ecommerce'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { H5, H6, H8 } from 'app-v2/design-systems/typography/titles'
import { sortOffersInKingdomLevel } from 'shared/utils'
import { SPACING } from 'app-v2/design-tokens'

const { lightGrey, grey, tertiary } = oldColors

type Props = {
    index?: number,
    minPoints: number,
    offers: Array<OfferType>,
    points: number,
    color?: string,
    fromEcommerce?: boolean,
    title?: string,
    openSelectionScreen?: (
        selectionScreenInfos: SelectionScreenInfos,
        error?: string
    ) => void,
    loadingPointsTransformation?: (loading: boolean) => void,
    collectMode?: string
}

const Level = (props: Props) => {
    const {
        index,
        minPoints,
        offers,
        points: userPoints,
        color: backgroundColor,
        fromEcommerce,
        title,
        openSelectionScreen,
        loadingPointsTransformation,
        collectMode
    } = props

    const { promotionsById, menusById, productsById } = useSelector(
        (state: RootState) => state.ecommerceCatalogState
    )

    const {
        unLockedOffers,
        lockedOffers,
        pointsBeforeLevelUnlocked
    } = useMemo(
        () =>
            sortOffersInKingdomLevel(
                offers,
                userPoints,
                minPoints,
                fromEcommerce ? collectMode : undefined
            ),
        [offers, userPoints, minPoints, fromEcommerce, collectMode]
    )

    const firstLevel = index === 0

    const isOfferAvailable = (promotionId: string) => {
        return doesPromotionHaveAvailableProductsOrMenus(
            promotionId,
            menusById,
            productsById,
            promotionsById
        )
    }

    const renderUnavailableOffer = () => (
        <UnavailableOfferText color={colors.fieryRed}>
            <FormattedMessage id="kingdom.points.offer.unavailable" />
        </UnavailableOfferText>
    )

    const renderOffer = (
        offer: OfferType,
        locked: boolean,
        firstLevel?: boolean
    ) => {
        const offerIsUnavailable =
            fromEcommerce && !isOfferAvailable(offer.promotionId)
        const lockedOffer = locked || offerIsUnavailable
        return (
            <Fragment>
                {!!offerIsUnavailable && renderUnavailableOffer()}
                <Offer
                    {...offer}
                    levelPoints={minPoints}
                    backgroundColor={backgroundColor}
                    fromEcommerce={fromEcommerce}
                    openSelectionScreen={openSelectionScreen}
                    loadingPointsTransformation={loadingPointsTransformation}
                    firstLevel={firstLevel}
                    locked={lockedOffer}
                />
            </Fragment>
        )
    }

    return (
        <Container fromEcommerce={fromEcommerce}>
            <Wrapper
                backgroundColor={backgroundColor}
                fromEcommerce={fromEcommerce}>
                <PointsContainer>
                    <H5>{minPoints}</H5>
                    <Icon
                        code={tokens.crown}
                        color={
                            firstLevel ? colors.meltyYellow : backgroundColor
                        }
                        size={40}
                        marginRight={26}
                        marginLeft={10}
                    />
                    {title && <H6>{title}</H6>}
                </PointsContainer>
                <OffersContainer>
                    {unLockedOffers.map((item: OfferType, index: number) => {
                        return (
                            <div key={index}>
                                {renderOffer(item, false, firstLevel)}
                            </div>
                        )
                    })}
                    <LockContainer>
                        {lockedOffers.length > 0 && (
                            <LockedLevel
                                totallyLocked={unLockedOffers.length === 0}>
                                <LockTextContainer>
                                    <Icon
                                        code={tokens.filledLock}
                                        size={25}
                                        color={colors.bbqBrown}
                                    />
                                    <H8 color={colors.bbqBrown}>
                                        <FormattedMessage
                                            id="kingdom.points.level.locked"
                                            values={{
                                                points: pointsBeforeLevelUnlocked
                                            }}
                                        />
                                    </H8>
                                </LockTextContainer>
                            </LockedLevel>
                        )}
                        {lockedOffers.length !== 0 &&
                            lockedOffers.map(
                                (item: OfferType, index: number) => {
                                    return (
                                        <div key={index}>
                                            {renderOffer(
                                                item,
                                                true,
                                                firstLevel
                                            )}
                                        </div>
                                    )
                                }
                            )}
                    </LockContainer>
                </OffersContainer>
            </Wrapper>
        </Container>
    )
}

const Container = styled.div`
    position: relative;
    background-color: ${props => (props.fromEcommerce ? tertiary : `white`)};
    border-radius: 10px;
`

const LockedLevel = styled.div`
    position: absolute;
    z-index: 100;
    flex: 1;
    opacity: 1;
    elevation: 10;
    margin-left: 55px;
    margin-top: -${SPACING.L};
`

const LockContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
`

const LockTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
`

const Wrapper = styled.div`
    position: relative;
    background-color: white;
    border-radius: 10px;
    ${media.medium`
        width: calc(80% - 128px);
    `};
    ${media.large`
         width: 100%;
    `};
`

const PointsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin: 0 0 ${SPACING.S} ${SPACING.XS};
`

const OffersContainer = styled.div`
    position: relative;
    display: flex;
    overflow-x: auto;
    ::-webkit-scrollbar {
        height: 6px;
        border-radius: 6px;
        background-color: ${lightGrey};
    }
    ::-webkit-scrollbar-thumb {
        background-color: ${grey};
        border-radius: 6px;
    }
`

const UnavailableOfferText = styled(H3)`
    padding-top: 50px;
    margin-bottom: -106px;
    margin-left: 15px;
    position: sticky;
    z-index: 10;
    ${media.medium`
        ${`${styleH3};
        padding-top: 55px;
        margin-left: 10px;
        max-width: 100px;
        `};

    `};
`

export { Level }
